/**
 * Scorecard of published values: approved indicators across regions or
 * districts, coloured by each indicator's own thresholds.
 */
import React, { useState } from 'react';
import {
  Alert, Box, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  Scorecard, ScorecardCell, ThresholdStatus, errorMessage, indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';
import { formatValue } from './ResultsTable';

const LAST_YEAR = new Date().getFullYear() - 1;
const YEARS = Array.from({ length: 6 }, (_, i) => LAST_YEAR + 1 - i);

const STATUS_STYLE: Record<ThresholdStatus, { bg: string; fg: string; label: string }> = {
  good: { bg: '#E6F4EA', fg: '#1E6B34', label: 'Good' },
  watch: { bg: '#FEF3E0', fg: '#8A5300', label: 'Watch' },
  poor: { bg: '#FCE8E6', fg: '#A3261B', label: 'Poor' },
};

type Ind = Scorecard['indicators'][number];

const Cell: React.FC<{ ind: Ind; cell?: ScorecardCell; previous?: number | null; bold?: boolean }> = ({
  ind, cell, previous, bold,
}) => {
  const style = cell?.status ? STATUS_STYLE[cell.status] : undefined;
  const value = cell?.value ?? null;
  const change = value !== null && previous !== undefined && previous !== null ? value - previous : null;
  return (
    <TableCell
      align="right"
      sx={{
        bgcolor: style?.bg, color: style?.fg, fontWeight: bold ? 700 : 500,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', borderLeft: 1, borderColor: 'divider',
      }}
    >
      <Tooltip
        title={cell ? `${ind.name}: ${formatValue(value, ind.unit, ind.decimals)}${style ? ` (${style.label})` : ''}`
          + `, ${cell.schools.toLocaleString()} schools${previous !== undefined && previous !== null
            ? `. Last year ${formatValue(previous, ind.unit, ind.decimals)}` : ''}` : 'Not published'}
      >
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {formatValue(value, ind.unit, ind.decimals)}
          {change !== null && change !== 0 && (change > 0
            ? <ArrowDropUp fontSize="small" sx={{ opacity: 0.7 }} aria-label="up from last year" />
            : <ArrowDropDown fontSize="small" sx={{ opacity: 0.7 }} aria-label="down from last year" />)}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

const IndicatorScorecard: React.FC = () => {
  const [year, setYear] = useState(LAST_YEAR);
  const [geoLevel, setGeoLevel] = useState('district');
  const { data, isLoading, isError, error } = useQuery(
    ['indicator-registry', 'scorecard', year, geoLevel],
    () => indicatorRegistryApi.scorecard(year, geoLevel),
  );

  const indicators = (data?.indicators || []).filter(i => i.published);
  const unpublished = (data?.indicators || []).filter(i => !i.published);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Scorecard</Typography>
          <Typography variant="body2" color="text.secondary">
            Published values of approved indicators. Colours follow each indicator's thresholds; arrows compare with the year before.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={e => setYear(Number(e.target.value))}>
              {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select label="Level" value={geoLevel} onChange={e => setGeoLevel(e.target.value)}>
              <MenuItem value="region">Region</MenuItem>
              <MenuItem value="district">District</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">{errorMessage(error)}</Alert>}

      {data && !indicators.length && (
        <Alert severity="info">Nothing is published for {year} yet. Publish values from an approved indicator in the Indicator Studio.</Alert>
      )}

      {data && indicators.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 160, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                  {geoLevel === 'region' ? 'Region' : 'District'}
                </TableCell>
                {indicators.map(ind => (
                  <TableCell key={ind.code} align="right" sx={{ minWidth: 110, verticalAlign: 'bottom' }}>
                    <Tooltip title={ind.name}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                          {ind.short_name || ind.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{ind.code}</Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>Zanzibar</TableCell>
                {indicators.map(ind => <Cell key={ind.code} ind={ind} cell={data.national[ind.code]} bold />)}
              </TableRow>
              {data.areas.map(area => (
                <TableRow key={area.code} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>{area.name}</TableCell>
                  {indicators.map(ind => (
                    <Cell
                      key={ind.code}
                      ind={ind}
                      cell={data.cells[ind.code]?.[area.code]}
                      previous={data.previous[ind.code]?.[area.code]}
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data && (
        <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {(Object.keys(STATUS_STYLE) as ThresholdStatus[]).map(s => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: STATUS_STYLE[s].bg, border: 1, borderColor: STATUS_STYLE[s].fg }} />
              <Typography variant="caption">{STATUS_STYLE[s].label}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">No colour: the indicator has no thresholds.</Typography>
          {unpublished.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Not published for {year}: {unpublished.map(i => i.code).join(', ')}.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default IndicatorScorecard;
